import {
  APPOINTMENT_SECRET_KEY,
  SANP_AI_API_KEY,
  CONVERSATION_ID_URL,
  EXTRACT_OPTIONS_URL,
  SYMPTOM_COLLECTOR_RTC_MODEL,
  SYMPTOM_COLLECTOR_TRANSCRIPTION_MODEL,
} from "../../envConstants";
import { languageOptions, OPENAI_SUPPORTED_LANGUAGES } from "./utils/constants";

export const fetchExtractOptions = async function (
  payload: any,
  jwtToken: string
) {
  const response = await fetch(`${EXTRACT_OPTIONS_URL}/extract-options`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwtToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  return data?.endOfConversation;
};

export const fetchAppointmentDetails = async function (
  appointmentId: string,
  jwtToken: string
) {
  const response = await fetch(
    `${CONVERSATION_ID_URL}/api/v1/receptionist-agent/appointment-status/${appointmentId}`,
    {
      method: "GET",
      headers: {
        "x-api-key": APPOINTMENT_SECRET_KEY || "",
        "Content-Type": "application/json",
      },
    }
  );
  const data = await response.json();
  return data;
};

export const fetchSCSummary = async function (payload: any, jwtToken: string) {
  const response = await fetch(
    `${CONVERSATION_ID_URL}/api/v1/receptionist-agent/get-symptoms`,
    {
      method: "POST",
      headers: {
        "x-api-key": APPOINTMENT_SECRET_KEY || "",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );
  const data = await response.json();
  return data;
};

export const getSummary = async (conversationId: string, jwtToken: string) => {
  try {
    const endpoint = `${CONVERSATION_ID_URL}/api/v1/agents/get-ner`;
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        _id: conversationId,
      }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting summary:", error);
    return null;
  }
};

export const saveConversationData = async (
  uuid: string,
  userTranscription: any,
  agentTranscription: any,
  conversationId: any,
  conversationChunks: any,
  transcribedParagraph: any,
  selectedLanguage: string,
  um_id: string,
  patient_unique_id: string,
  hm_id: string,
  pam_id: string,
  jwtToken: string
) => {
  try {
    const endpoint = `${CONVERSATION_ID_URL}/api/v1/agents/save-conversation`;
    const formdata = new FormData();

    const audioBlob = new Blob(conversationChunks, {
      type: "audio/webm",
    });

    const audioFile = audioBlob;

    const audioFilename = [`${uuid}.webm`];

    if (conversationChunks.length > 0) {
      formdata.append("audio_file", audioFile, `${uuid}.webm`);
      formdata.append("audio_filename", JSON.stringify(audioFilename));
    } else {
      const data: any = null;
      formdata.append("audio_file", data);
      formdata.append("audio_filename", data);
    }

    formdata.append("user_transcriptions", JSON.stringify(userTranscription));
    formdata.append("agent_transcriptions", JSON.stringify(agentTranscription));
    formdata.append("summary", JSON.stringify(transcribedParagraph));
    formdata.append("um_id", um_id || "524");
    formdata.append("patient_unique_id", patient_unique_id);
    formdata.append("hm_id", hm_id || "432");
    formdata.append("pam_id", pam_id || "9266");
    formdata.append("language", selectedLanguage);

    if (conversationId) {
      formdata.append("_id", conversationId);
    }

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
      },
      body: formdata,
    });

    const data = await response.json();
    return data?.result;
  } catch (error) {
    console.error("Error saving conversation:", error);
    return null;
  }
};

export const createSession = async (
  language: string | null,
  transcribedParagraph: any,
  id: any,
  jwtToken: string
) => {
  try {
    const endpoint = `${CONVERSATION_ID_URL}/api/v1/agents/openai-realtime`;

    // Find the selected language object to get its voice
    const selectedLang = languageOptions.find(
      (lang) => lang.value === language
    );
    const voice = selectedLang?.voice || "alloy"; // default to alloy if not found

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        language,
        voice,
        summary: transcribedParagraph,
        _id: id,
      }),
    });
    const data = await response.json();
    return data?.result?.client_secret?.value;
  } catch (error) {
    console.error("Error creating session:", error);
    return null;
  }
};

export const establishWebRtcConnection = async (
  ephemeralKey: string,
  offer: RTCSessionDescriptionInit
) => {
  try {
    const endpoint = `https://api.openai.com/v1/realtime?model=${SYMPTOM_COLLECTOR_RTC_MODEL}`;
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${ephemeralKey}`,
        "Content-Type": "application/sdp",
      },
      body: offer.sdp,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("WebRTC connection failed:", response.status, errorText);
      throw new Error(`Connection failed: ${response.status} ${errorText}`);
    }

    return await response.text();
  } catch (error) {
    console.error("Error establishing WebRTC connection:", error);
    throw error;
  }
};

export async function startAzureOpenAISession(
  agentVoice: any,
  instructions: any,
  languageCode: string
) {
  try {
    const DEPLOYMENT = "sympton-collector-realtime";
    const SESSIONS_URL =
      "https://sanp-ai.openai.azure.com/openai/realtimeapi/sessions?api-version=2025-04-01-preview";
    const API_KEY = SANP_AI_API_KEY || '';
    const response = await fetch(SESSIONS_URL, {
      method: "POST",
      headers: {
        "api-key": API_KEY,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: DEPLOYMENT,
        input_audio_noise_reduction: {
          type: "near_field",
        },
        input_audio_transcription: {
          model: SYMPTOM_COLLECTOR_TRANSCRIPTION_MODEL,
          ...(OPENAI_SUPPORTED_LANGUAGES.includes(languageCode || "en") && {
            language: languageCode || "en",
          }),
        },
        instructions: instructions,
        voice: agentVoice,
        tool_choice: "auto",
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed`);
    }

    const data = await response.json();
    const ephemeralKey = data.client_secret?.value;

    return ephemeralKey;
  } catch (error) {
    console.error("Error fetching ephemeral key:", error);
    return null;
  }
}

export async function getInstructions(
  selectedLanguage: string,
  chat_history: any,
  agent_name: string,
  agent_gender: string
) {
  const headers = {
    "x-api-key": APPOINTMENT_SECRET_KEY || "",
  };
  const endpoint = `${CONVERSATION_ID_URL}/api/v1/prompts/SYMPTOMS_COLLECTOR_VIOCE_MODE?language=${selectedLanguage}&agent_name=${agent_name}&agent_gender=${agent_gender}&chat_history=${chat_history}&prefix=%24&open={&close=}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData?.prompt ?? null;
  } catch (err) {
    console.error("Failed to handle get_specialities:", err);
    return [];
  }
}

// Function to get conversation ID
export const getConversationId = async (body: {
  um_id: string;
  pam_id: string;
  hm_id: string;
  patient_unique_id: string;
  jwtToken: string;
}) => {
  const { um_id, pam_id, hm_id, patient_unique_id, jwtToken } = body;

  const headers = {
    Authorization: `Bearer ${jwtToken}`,
    "Content-Type": "application/json",
  };

  const endpoint = `${CONVERSATION_ID_URL}/api/v1/agents/get-conversation-id`;
  const payload = {
    um_id,
    pam_id,
    hm_id,
    patient_unique_id,
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData ?? null;
  } catch (err) {
    console.error("Failed to get conversation ID:", err);
    return null;
  }
};
